import * as Yup from 'yup';

import { UserAuth } from '../users/User';
import { ResponseOrdersProps } from '../orders/Orders';
import { TableBasic } from '../tables/Tables';
import { AccountSimplifiedPlanValues } from '../accounts/interfaces';
import {
  GeneralSale,
  ResponseGeneralSale,
  GeneralSaleProduct,
} from './GeneralSales';
import {
  CabysCatalog,
  EconomicActivity,
  LatestCabysCodes,
} from '../settings/Setting';
import {
  GetCabysCatalogArgs,
  LatestCabysCodesArgs,
  GetEconomicActivityRestaurantgArgs,
} from '../../store/actions/settings/SettingTypes';
import {
  ElectronicInvoice,
  electronicInvoiceInitialValues,
} from '../orders/oldInterfaces';
import {
  ElectronicInvoiceType,
  TypeDocumentIdSpanish,
} from '../../utils/enums';
import { CABYS_DEFAULT } from '../../utils/constants';

export const fieldNames = {
  isInvoice: 'isInvoice',
  name: 'electronicInvoice.name',
  documentId: 'electronicInvoice.documentId',
  documentType: 'electronicInvoice.documentType',
  email: 'electronicInvoice.email',
  phone: 'electronicInvoice.phone',
  electronicInvoice: 'electronicInvoice',
};

export interface OrdersGeneralSalesProps {
  orders: ResponseOrdersProps;
  listOrders: (
    numberPage: number,
    limit: number,
    accountId: string,
    search: string,
    filter: string
  ) => Promise<void>;
  tables: TableBasic[];
  getTables: (accountId: string) => Promise<void>;
  user: UserAuth;
}

export interface GeneralSalesScreenProps {
  user: UserAuth;
  generalSales: ResponseGeneralSale;
  listGeneralSale: (
    numberPage: number,
    limit: number,
    accountId: string,
    search: string,
    filter: string
  ) => Promise<void>;
  deleteGeneralSale: (
    form: {
      id: string;
      accountId: string;
    },
    callbackSuccess: Function,
    callbackError: Function
  ) => Promise<void>;
}

export interface CreateGeneralSalesProps {
  user: UserAuth;
  createGeneralSale: (
    form: object,
    callbackSuccess: (generalSale: GeneralSale) => void,
    callbackError: Function
  ) => Promise<void>;
  cabysCatalog: CabysCatalog[];
  getCabysCatalog: ({ ...args }: GetCabysCatalogArgs) => Promise<void>;
  latestCabysCodes: LatestCabysCodes[];
  getLatestCabysCodes: ({ ...args }: LatestCabysCodesArgs) => Promise<void>;
  economicActivityRestaurant: EconomicActivity[];
  getEconomicActivityRestaurant: ({
    ...args
  }: GetEconomicActivityRestaurantgArgs) => Promise<void>;
}

export interface EditGeneralSalesProps {
  user: UserAuth;
  updateGeneralSale: (
    form: object,
    callbackSuccess: Function,
    callbackError: Function
  ) => Promise<void>;
  getGeneralSale: (
    id: string,
    accountId: string,
    callback: Function
  ) => Promise<void>;
  generalSale: GeneralSale;
  cabysCatalog: CabysCatalog[];
  getCabysCatalog: ({ ...args }: GetCabysCatalogArgs) => Promise<void>;
  latestCabysCodes: LatestCabysCodes[];
  getLatestCabysCodes: ({ ...args }: LatestCabysCodesArgs) => Promise<void>;
  economicActivityRestaurant: EconomicActivity[];
  getEconomicActivityRestaurant: ({
    ...args
  }: GetEconomicActivityRestaurantgArgs) => Promise<void>;
}

export interface ProductFormValues extends GeneralSaleProduct {
}

export interface GeneralSalesFormValues {
  accountId?: string;
  amount: number;
  observations: string;
  products: ProductFormValues[];
  id?: string;
  _id?: string;
  customerName: string;
  date: string;
  reference?: string;
  isInvoice?: string;
  electronicInvoice?: ElectronicInvoice;
  check: string;
}

export const initialValues: GeneralSalesFormValues = {
  accountId: '',
  amount: 0,
  observations: '',
  products: [],
  customerName: '',
  date: '',
  reference: '',
  isInvoice: ElectronicInvoiceType.ELECTRONIC_INVOICE,
  electronicInvoice: electronicInvoiceInitialValues,
  check: 'NO',
};

export const validationSchema: Yup.SchemaOf<GeneralSalesFormValues> =
  Yup.object({
    accountId: Yup.string(),
    amount: Yup.number()
      .typeError('Debe ser un número, separado por puntos. Ej: 0.0')
      .test('Validacion de Cantidad', 'Debe ser mayor a 0', (value) =>
        value <= 0 ? false : true
      )
      .required('Este campo es requerido'),
    observations: Yup.string(),
    products: Yup.array(
      Yup.object().shape({
        name: Yup.string(),
        qty: Yup.number().typeError(
          'Debe ser un número, separado por puntos. Ej: 0.0'
        ),
        unitPrice: Yup.number().typeError(
          'Debe ser un número, separado por puntos. Ej: 0.0'
        ),
        total: Yup.number().typeError(
          'Debe ser un número, separado por puntos. Ej: 0.0'
        ),
        notes: Yup.string(),
        cabys: Yup.string().notRequired().nullable(),
        ivaPercentage: Yup.number()
          .typeError('Debe ser un número, separado por puntos. Ej: 0.0')
          .test('Validacion de IVA', 'Debe ser mayor o igual a 0', (value) =>
            value < 0 ? false : true
          )
          .notRequired()
          .nullable(),
        ivaFee: Yup.number()
          .typeError('Debe ser un número, separado por puntos. Ej: 0.0')
          .test('Validacion de IVA', 'Debe ser mayor o igual a 0', (value) =>
            value < 0 ? false : true
          )
          .notRequired()
          .nullable(),
        check: Yup.string().notRequired().nullable(),
        economicActivityCode: Yup.string().required('Este campo es requerido'),
      })
    ).min(1, 'Se requiere algregar al menos un producto'),
    id: Yup.string(),
    customerName: Yup.string().required('Este campo es requerido'),
    date: Yup.string().required('Este campo es requerido'),
    reference: Yup.string(),
    _id: Yup.string(),
    check: Yup.string(),
    isInvoice: Yup.string(),
    electronicInvoice: Yup.object()
      .shape({
        documentType: Yup.string(),
        documentId: Yup.string(),
        name: Yup.string(),
        email: Yup.string(),
        phone: Yup.string(),
      })
      .when(['check', 'isInvoice'], {
        is: (check: string, isInvoice: string) =>
          check === 'SI' &&
          isInvoice &&
          isInvoice === ElectronicInvoiceType.ELECTRONIC_INVOICE,
        then: Yup.object().shape({
          documentType: Yup.string().required('Este campo es requerido'),
          documentId: Yup.string().when(
            'documentType',
            (documentType: string) => {
              if (
                documentType === TypeDocumentIdSpanish.CI ||
                documentType === TypeDocumentIdSpanish.NITE
              ) {
                return Yup.string()
                  .matches(
                    /^[1-9]\d{8}$/,
                    'Debe de contener 9 dígitos, sin cero al inicio y sin caracteres especiales o letras'
                  )
                  .required('Este campo es requerido');
              } else if (documentType === TypeDocumentIdSpanish.CJ) {
                return Yup.string()
                  .matches(
                    /^[1-9]\d{9}$/,
                    'Debe de contener 10 dígitos, sin cero al inicio y sin caracteres especiales o letras'
                  )
                  .required('Este campo es requerido');
              } else if (documentType === TypeDocumentIdSpanish.DIMEX) {
                return Yup.string()
                  .matches(
                    /^[1-9]\d{10,11}$/,
                    'Debe de contener 10 ó 11 dígitos, sin cero al inicio y sin caracteres especiales o letras'
                  )
                  .required('Este campo es requerido');
              } else {
                return Yup.string().required('Este campo es requerido');
              }
            }
          ),
          name: Yup.string().required('Este campo es requerido'),
          email: Yup.string()
            .email('Debe introducir un email válido')
            .required('Este campo es requerido'),
          phone: Yup.string()
            .matches(
              /^[0-9]{8}$/,
              'Debe de contener 8 dígitos, sin caracteres especiales o letras'
            )
            .required('Este campo es requerido'),
        }),
        otherwise: Yup.object().shape({
          documentType: Yup.string(),
          documentId: Yup.string(),
          name: Yup.string(),
          email: Yup.string(),
          phone: Yup.string(),
        }),
      })
      .nullable(),
  });

export const initialValuesProduct: ProductFormValues = {
  name: '',
  qty: 1,
  unitPrice: 0,
  total: 0,
  notes: '',
  cabys: CABYS_DEFAULT,
  ivaPercentage: 13,
  ivaFee: 0,
  check: 'NO',
  economicActivityCode: '',
};

export const validationSchemaProduct: Yup.SchemaOf<ProductFormValues> =
  Yup.object({
    name: Yup.string().required('Este campo es requerido'),
    qty: Yup.number()
      .typeError('Debe ser un número, separado por puntos. Ej: 0.0')
      .test('Validacion de Cantidad', 'Debe ser mayor a 0', (value) =>
        value <= 0 ? false : true
      )
      .required('Este campo es requerido'),
    unitPrice: Yup.number()
      .typeError('Debe ser un número, separado por puntos. Ej: 0.0')
      .test('Validacion de Cantidad', 'Debe ser mayor a 0', (value) =>
        value <= 0 ? false : true
      )
      .required('Este campo es requerido'),
    total: Yup.number()
      .typeError('Debe ser un número, separado por puntos. Ej: 0.0')
      .test('Validacion de Cantidad', 'Debe ser mayor a 0', (value) =>
        value <= 0 ? false : true
      )
      .required('Este campo es requerido'),
    notes: Yup.string(),
    cabys: Yup.string().when('check', (check: string) => {
      if (check === 'SI') {
        return Yup.string().required('Este campo es requerido');
      }
      return Yup.string().notRequired().nullable();
    }),
    ivaPercentage: Yup.number().when('check', (check: string) => {
      if (check === 'SI') {
        return Yup.number()
          .typeError('Debe ser un número, separado por puntos. Ej: 0.0')
          .test('Validacion de IVA', 'Debe ser mayor o igual a 0', (value) =>
            value < 0 ? false : true
          )
          .required('Este campo es requerido');
      }
      return Yup.number().notRequired().nullable();
    }),
    ivaFee: Yup.number().when('check', (check: string) => {
      if (check === 'SI') {
        return Yup.number()
          .typeError('Debe ser un número, separado por puntos. Ej: 0.0')
          .test('Validacion de IVA', 'Debe ser mayor o igual a 0', (value) =>
            value < 0 ? false : true
          )
          .required('Este campo es requerido');
      }
      return Yup.number().notRequired().nullable();
    }),
    check: Yup.string().notRequired().nullable(),
    economicActivityCode: Yup.string().required('Este campo es requerido'),
  });

export const GeneralSaleValues: GeneralSale = {
  _id: '',
  accountId: AccountSimplifiedPlanValues,
  customerName: '',
  amount: 0,
  observations: '',
  date: '',
  reference: '',
  products: [],
  createdAt: '',
  deletedAt: '',
  __v: 0,
  electronicInvoice: null,
  documentType: '',
};
